import type { LocalizedText } from '../permissions.ts';
import type { ThemeFont, ThemeMenu } from '../theme.ts';
import type { PaletteColors } from './colors.ts';
import { paletteSpec } from './palettes.ts';

/**
 * Estilos prontos (ADR 0126): os cinco pacotes que existiam antes do Estúdio de estilos. Cada um é
 * agora uma combinação de layout, paleta e acabamento (ADR 0128): as cores vêm da paleta de mesmo
 * id, nunca de uma cópia. Ficam para a Configuração › Estilo de hoje e para ler as escolhas já
 * gravadas; o Estúdio passa a oferecer os três eixos separados.
 *
 * Os ids `clear`, `warm` e `dark` são os mesmos das opções da entrevista do Smart Builder.
 */

export const STYLE_DENSITIES = ['comfortable', 'compact'] as const;
export type StyleDensity = (typeof STYLE_DENSITIES)[number];

export interface StyleSpec extends PaletteColors {
  id: string;
  name: LocalizedText;
  description: LocalizedText;
  radius: { sm: string; md: string };
  panel: 'border' | 'shadow';
  /** O que o estilo já traz marcado; o cliente pode trocar cada um. */
  defaults: { accent: string; font: ThemeFont; menu: ThemeMenu; density: StyleDensity };
}

function colorsOf(id: string): PaletteColors {
  const p = paletteSpec(id)!;
  return { base: p.base, sidebar: p.sidebar, dark: p.dark };
}

export const READY_STYLES = [
  {
    id: 'beadev',
    name: { 'pt-BR': 'Beadev', en: 'Beadev' },
    description: {
      'pt-BR': 'O de fábrica: lateral escura, destaque verde-limão e a letra da marca.',
      en: 'The factory one: dark side menu, lime accent and the brand font.',
    },
    ...colorsOf('beadev'),
    radius: { sm: '6px', md: '8px' },
    panel: 'shadow',
    defaults: { accent: '#c4ef2f', font: 'base', menu: 'side', density: 'compact' },
  },
  {
    id: 'clear',
    name: { 'pt-BR': 'Claro e leve', en: 'Light and airy' },
    description: {
      'pt-BR': 'Fundo branco e menu na lateral. Funciona para quase tudo.',
      en: 'White background and side menu. Works for almost anything.',
    },
    ...colorsOf('clear'),
    radius: { sm: '6px', md: '8px' },
    panel: 'shadow',
    defaults: { accent: '#2563eb', font: 'modern', menu: 'side', density: 'comfortable' },
  },
  {
    id: 'top',
    name: { 'pt-BR': 'Topo limpo', en: 'Clean top bar' },
    description: {
      'pt-BR': 'Menu numa faixa no topo e a tela inteira para o trabalho. Bom para poucas áreas.',
      en: 'Menu in a top bar and the whole screen for work. Good for a few areas.',
    },
    ...colorsOf('top'),
    radius: { sm: '6px', md: '8px' },
    panel: 'border',
    defaults: { accent: '#0f766e', font: 'modern', menu: 'top', density: 'compact' },
  },
  {
    id: 'warm',
    name: { 'pt-BR': 'Acolhedor', en: 'Warm' },
    description: {
      'pt-BR': 'Tons de papel, letra clássica e menu no topo.',
      en: 'Paper tones, classic font and top menu.',
    },
    ...colorsOf('warm'),
    radius: { sm: '8px', md: '12px' },
    panel: 'shadow',
    defaults: { accent: '#c2410c', font: 'classic', menu: 'top', density: 'comfortable' },
  },
  {
    id: 'dark',
    name: { 'pt-BR': 'Escuro', en: 'Dark' },
    description: {
      'pt-BR': 'Fundo escuro e mais coisas na tela.',
      en: 'Dark background and more on screen.',
    },
    ...colorsOf('dark'),
    radius: { sm: '6px', md: '8px' },
    panel: 'border',
    defaults: { accent: '#7c3aed', font: 'modern', menu: 'side', density: 'compact' },
  },
] as const satisfies readonly StyleSpec[];

export type StyleId = (typeof READY_STYLES)[number]['id'];
export const STYLE_IDS = READY_STYLES.map((s) => s.id) as [StyleId, ...StyleId[]];

/** Cores de destaque sugeridas na tela de Estilo (o cliente pode usar qualquer outra). */
export const SUGGESTED_ACCENTS = [
  '#c4ef2f',
  '#2563eb',
  '#0f766e',
  '#16a34a',
  '#c2410c',
  '#dc2626',
  '#db2777',
  '#7c3aed',
] as const;

export function styleSpec(id: string): StyleSpec | undefined {
  return (READY_STYLES as readonly StyleSpec[]).find((s) => s.id === id);
}
